import { useCallback } from 'react';
import { FlatList, type ListRenderItemInfo, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Badge } from '../../../components/primitives/Badge';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { LeadResult } from '../types';

interface SegmentResultsProps {
  leads: LeadResult[];
  segmentName: string;
}

function getScoreBadgeColor(score: number): 'green' | 'amber' | 'red' {
  if (score >= 7) return 'green';
  if (score >= 4) return 'amber';
  return 'red';
}

export function SegmentResults({ leads, segmentName }: Readonly<SegmentResultsProps>) {
  const { t } = useTranslation();

  const renderLead = useCallback(
    ({ item }: ListRenderItemInfo<LeadResult>) => {
      const score = item.customFields?.score == null ? undefined : Number(item.customFields.score);

      return (
        <Box
          borderRadius="lg"
          padding="md"
          backgroundColor={colors.background.tertiary}
          style={styles.card}
        >
          <VStack spacing="sm">
            <HStack spacing="sm" style={styles.header}>
              <VStack spacing="xxs" style={styles.nameContainer}>
                <Text variant="body" color={colors.text.primary} style={styles.name}>
                  {item.fullName || 'Unknown'}
                </Text>
                {item.jobTitle && (
                  <Text variant="caption" color={colors.text.secondary}>
                    {item.jobTitle}
                  </Text>
                )}
                {item.company && (
                  <Text variant="caption" color={colors.text.tertiary}>
                    {item.company}
                  </Text>
                )}
              </VStack>
              {score != null && (
                <Badge color={getScoreBadgeColor(score)} variant="subtle">
                  {t('linkedinSearch.results.score')}: {score}
                </Badge>
              )}
            </HStack>

            <HStack spacing="md">
              {item.email && (
                <Text variant="caption" color={colors.text.disabled} numberOfLines={1}>
                  {item.email}
                </Text>
              )}
              {item.currentStage && (
                <Badge color="cyan" variant="outline">
                  {item.currentStage.name}
                </Badge>
              )}
            </HStack>
          </VStack>
        </Box>
      );
    },
    [t],
  );

  return (
    <VStack spacing="md">
      <HStack spacing="sm" style={styles.headerRow}>
        <Text variant="body" color={colors.text.primary} style={styles.headerTitle}>
          {t('linkedinSearch.results.title')}: {segmentName}
        </Text>
        <Badge color="cyan" variant="subtle">
          {leads.length} {t('linkedinSearch.results.leads')}
        </Badge>
      </HStack>
      <FlatList
        data={leads}
        keyExtractor={(item) => item.id}
        renderItem={renderLead}
        contentContainerStyle={styles.list}
        accessibilityRole="list"
        accessibilityLabel="Search results"
      />
    </VStack>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 8,
    paddingBottom: 24,
  },
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  header: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontWeight: '600',
  },
  nameContainer: {
    flex: 1,
  },
  name: {
    fontWeight: '600',
  },
});
